"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Table from "../../../../components/Table";
import Button from "../../../../components/Button";
import NameSearchFilter from "../../../../components/name-search-filter";
import Pagination from "../../../../components/Pagination";
import styles from "../../../../css/components/overlaymodal-animations.module.css";
import csStyles from "../css/customer-selection.module.css";
import { fetchPatients as fetchPatientsApi, fetchProfileInfo, fetchProfileFamily } from "../../../../api/dispensary/script/processScriptApi.js";

export default function CustomerSelect({ isOpen, onClose, onSelect, scriptData = {}, scriptFile, profileNo }) {
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const navigate = useNavigate();

  // Source script data from session storage if not provided via props
  const storedScript = (() => {
    try {
      const s = sessionStorage.getItem("currentScript");
      return s ? JSON.parse(s) : {};
    } catch {
      return {};
    }
  })();
  const baseScriptData = (scriptData && Object.keys(scriptData).length > 0) ? scriptData : storedScript;

  const fetchPatients = async (search = "") => {
    try {
      const list = await fetchPatientsApi(search);
      const rows = (Array.isArray(list) ? list : []).map((p) => ({
        surname: p.surname,
        initial: p.initial,
        title: p.title,
        address: p.address,
        profileNo: String(p.profilenumber),
        // Ensure we preserve a patient identifier so downstream
        // views can resolve address / phone / medical aid
        patientId: p.patientid || p.patientId || p.id,
      }));
      setData(rows);
      setCurrentPage(1);
    } catch (e) {
      setData([]);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    if (profileNo) {
      (async () => {
        try {
          const list = await fetchProfileFamily(profileNo);
          const rows = (Array.isArray(list) ? list : []).map((m, idx) => {
            const match = (m.label || "").match(/^\s*(.+?)\s+([A-Za-z])\.?\s*\(([^)]+)\)/);
            const surname = match ? match[1] : (m.label || "");
            const initial = match ? (match[2] || "").toUpperCase() : "";
            const title = match ? match[3] : "";
            const role = idx === 0 ? "Main" : "Dependent";
            return {
              member: m.label,
              surname,
              initial,
              title,
              role,
              patientId: m.id,
              homeAddress: m.homeAddress || "",
              contactNumber: m.contactNumber || "",
              profileNo: String(profileNo),
            };
          });

          // The family endpoint returns main member first (ISBENEFICIARY asc).
          // Use that main member's patientId for ALL rows as mainPatientId so
          // downstream profile lookups (getProfileByPatient) always target the
          // main member, while the selected row still reflects the actual
          // beneficiary in labels.
          const mainPatientId = rows[0]?.patientId || null;
          const enriched = rows.map((r) => ({
            ...r,
            mainPatientId: mainPatientId || r.patientId,
          }));

          setData(enriched);
          setCurrentPage(1);
        } catch {
          setData([]);
        }
      })();
    } else {
      fetchPatients("");
    }
  }, [isOpen, profileNo]);

  const handleSearch = (e) => {
    const term = e.target.value || "";
    if (!profileNo) fetchPatients(term);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const totalPages = Math.ceil(data.length / itemsPerPage);
  const paginatedData = data.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const columnsForProfiles = [
    { header: "Profile No.", accessor: "profileNo" },
    { header: "Surname", accessor: "surname" },
    { header: "Initial", accessor: "initial" },
    { header: "Title", accessor: "title" },
    { header: "Address", accessor: "address" },
    {
      header: "Action",
      accessor: "action",
      render: (value, row) => (
        <Button
          variant="primary"
          onClick={async () => {
            if (typeof onSelect === "function") {
              onSelect(row);
            }
            let practitioner = baseScriptData.practitioner || "";
            let practitionerId = baseScriptData.practitionerId || null;
            try {
              const info = await fetchProfileInfo(row.profileNo);
              const raw = info?.practitioner ?? info?.mainMember?.practitioner ?? null;
              const prId = info?.practitionerid || info?.practitionerId || info?.mainMember?.practitionerid || info?.mainMember?.practitionerId;
              let display = "";

              if (raw && typeof raw === "object") {
                const title = raw.title || "";
                const first = raw.firstname || raw.firstName || "";
                const last = raw.surname || raw.lastName || "";
                const name = [title, first, last].filter(Boolean).join(" ").trim();
                display = name || raw.practicenumber || raw.practiceNumber || String(prId || "");
              } else if (typeof raw === "string") {
                display = raw;
              } else if (prId != null) {
                display = String(prId);
              }
              practitioner = display || practitioner;
              if (prId != null) {
                practitionerId = prId;
              }
            } catch {}
            const drugs = Array.isArray(baseScriptData.drugs) ? baseScriptData.drugs : [];
            const updatedScriptData = {
              ...baseScriptData,
              patient: `${row.surname} ${row.initial} (${row.profileNo})`,

              // Carry identifiers so scriptHistoryDetails can fetch
              // the correct profile and main member details
              patientId: row.patientId || baseScriptData.patientId,
              profileNo: row.profileNo || baseScriptData.profileNo,
              paymentType: baseScriptData.paymentType || "Card",
              drugs,
              practitioner,
              practitionerId,
              medicalAidNumber: baseScriptData.medicalAidNumber || "",

              isChronic: !!baseScriptData.isChronic,
              isRepeat: !!baseScriptData.isRepeat,
              repeatCount: baseScriptData.repeatCount || 0,
              scriptType: baseScriptData.scriptType || "PR",
              scriptNumber: baseScriptData.scriptNumber || baseScriptData.scriptNo || undefined,
              scriptNo: baseScriptData.scriptNo || baseScriptData.scriptNumber || undefined,
              totalItems: drugs.length,
            };
            console.log("CustomerSelect profile selection", { row, updatedScriptData });

            try { sessionStorage.setItem("currentScript", JSON.stringify(updatedScriptData)); } catch {}
            navigate("/customer/dispensary/price-script-payment-summary", { state: { scriptData: updatedScriptData, scriptFile } });
          }}
        >
          Select
        </Button>
      ),
    },
  ];

  const columnsForFamily = [
    { header: "Surname", accessor: "surname" },
    { header: "Initial", accessor: "initial" },
    { header: "Title", accessor: "title" },
    { header: "Role", accessor: "role" },
    {
      header: "Action",
      accessor: "action",
      render: (value, row) => (
        <Button
          variant="primary"
          onClick={async () => {
            if (typeof onSelect === "function") {
              onSelect(row);
            }
            let practitioner = scriptData.practitioner || "";
            let practitionerId = baseScriptData.practitionerId || null;
            try {
              const info = await fetchProfileInfo(row.profileNo);
              const raw = info?.practitioner ?? info?.mainMember?.practitioner ?? null;
              const prId =
                info?.practitionerid ||
                info?.practitionerId ||
                info?.mainMember?.practitionerid ||
                info?.mainMember?.practitionerId;
              let display = "";

              if (raw && typeof raw === "object") {
                const title = raw.title || "";
                const first = raw.firstname || raw.firstName || "";
                const last = raw.surname || raw.lastName || "";
                const name = [title, first, last].filter(Boolean).join(" ").trim();
                display = name || raw.practicenumber || raw.practiceNumber || String(prId || "");
              } else if (typeof raw === "string") {
                display = raw;
              } else if (prId != null) {
                display = String(prId);
              }
              practitioner = display || practitioner;
              if (prId != null) {
                practitionerId = prId;
              }
            } catch {}

            const drugs = Array.isArray(baseScriptData.drugs) ? baseScriptData.drugs : [];
            const updatedScriptData = {
              ...baseScriptData,
              patient: `${row.member} (${row.profileNo})`,

              // mainPatientId keeps the membership-level main member,
              // but patientId is the selected beneficiary.
              mainPatientId:
                row.mainPatientId ||
                baseScriptData.mainPatientId ||
                baseScriptData.patientId,
              patientId: row.patientId || baseScriptData.patientId,
              profileNo: row.profileNo || baseScriptData.profileNo,

              // Beneficiary-specific contact details
              addressLines: row.homeAddress
                ? [row.homeAddress]
                : baseScriptData.addressLines || [],
              phone: row.contactNumber || baseScriptData.phone || "",
              paymentType: baseScriptData.paymentType || "Card",
              drugs,
              practitioner,
              practitionerId,
              medicalAidNumber: baseScriptData.medicalAidNumber || "",
              isChronic: !!baseScriptData.isChronic,
              isRepeat: !!baseScriptData.isRepeat,
              repeatCount: baseScriptData.repeatCount || 0,
              scriptType: baseScriptData.scriptType || "PR",
              scriptNumber:
                baseScriptData.scriptNumber || baseScriptData.scriptNo || undefined,
              scriptNo:
                baseScriptData.scriptNo || baseScriptData.scriptNumber || undefined,
              totalItems: drugs.length,
            };

            console.log("CustomerSelect family selection", { row, updatedScriptData });
            console.log("Beneficiary address:", row.homeAddress);
            console.log("Beneficiary phone:", row.contactNumber);

            try {
              sessionStorage.setItem(
                "currentScript",
                JSON.stringify(updatedScriptData)
              );
            } catch {}
            navigate("/customer/dispensary/price-script-payment-summary", {
              state: { scriptData: updatedScriptData, scriptFile },
            });
          }}
        >
          Select
        </Button>
      ),
    },
  ];

  if (!isOpen) return null;
  return (
    <div
      className={`${styles.modalOverlay} ${isOpen ? styles.show : styles.hide}`}
      onClick={onClose}
    >
      <div
        className={`${styles.modalContent} ${isOpen ? styles.show : styles.hide}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button className={styles.closeButton} onClick={onClose}>
          ×
        </button>
        <div className={csStyles.modalInner}>
          <div className={csStyles.panel}>
            <div className={csStyles.headerRow}>
              <div className={csStyles.searchArea}>
                {!profileNo && <NameSearchFilter onChange={handleSearch} />}
              </div>
            </div>

            <div className={csStyles.body}>
              <div className={csStyles.tableWrapper}>
                <Table columns={profileNo ? columnsForFamily : columnsForProfiles} data={paginatedData} />
              </div>
              <div className={csStyles.paginationBar}>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}